// Home (visual refresh, 2026-11-04): one clear work area - what is waiting for you and
// your permits - with who you are acting as, Help & Learning and the suggested
// walkthrough secondary in the side column. The walkthrough is a disclosure so it never
// competes with pending work. Nothing here invents counts or actions.
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ErrorBanner } from '@/components/evidence'
import { Badge, Banner, Card, cx, EmptyState, KeyValue, PageHeader, SectionTitle, Spinner } from '@/components/ui'
import type { Me, PermitSummary } from '@/domain/types'
import { fmtDate, fmtDateTime } from '@/lib/format'
import { isDemoMode } from '@/lib/env'
import { useLearningProgress, useMe, usePermits, useSession } from '@/lib/queries'
import { tutorialContent } from '@/features/learning'
import { progressSentence } from '@/features/learning-host/overviewModel'
import { credentialKindLabel } from '@/features/demo/personas'
import { LinkButton } from './LinkButton'
import { PermitCard } from './PermitCard'
import { canStartPermit, credentialStatusTone, humanise, myPermits, roleLabel, waitingForMe } from './permitList'
import { WALKTHROUGH, WALKTHROUGH_DATA } from './walkthrough'

const YOUR_PERMITS_SHOWN = 6

export function HomePage() {
  const session = useSession()
  const me = useMe()
  const permits = usePermits()
  const list = permits.data ?? []
  const waiting = waitingForMe(list)
  const mine = myPermits(list)
  const canStart = me.data ? canStartPermit(me.data) : false
  const admin = Boolean(me.data?.global_capabilities?.includes('administer_accounts'))

  return (
    <div>
      <PageHeader
        title="Your permit workspace"
        subtitle="The work waiting for you, your next action and your permits in one place."
        actions={canStart ? <LinkButton to="/permits/new">New permit</LinkButton> : null}
      />
      <div className="va-home-grid">
        <div className="va-work-column">
          <Card>
            <SectionTitle aside={permits.data ? <Badge tone={waiting.length > 0 ? 'brand' : 'neutral'}>{waiting.length}</Badge> : null}>
              Waiting for you
            </SectionTitle>
            <PermitsBlock q={permits} items={waiting} empty="Nothing is waiting for you right now." />
          </Card>
          <Card>
            <SectionTitle
              aside={
                <Link to="/permits?mine=1" className="tap inline-flex items-center text-sm font-semibold text-brand-dark underline">
                  All permits
                </Link>
              }
            >
              Your permits
            </SectionTitle>
            <PermitsBlock q={permits} items={mine.slice(0, YOUR_PERMITS_SHOWN)} empty="You are not named on any permit yet." compact />
            {mine.length > YOUR_PERMITS_SHOWN ? (
              <div className="mt-3">
                <LinkButton to="/permits?mine=1" variant="secondary">
                  See all {mine.length} of your permits
                </LinkButton>
              </div>
            ) : null}
          </Card>
        </div>
        <aside className="va-help-column" aria-label="Your details and help">
          <Greeting me={me} />
          <LearningCard admin={admin} />
          {isDemoMode ? <WalkthroughCard activePersona={session.activePersona} /> : null}
        </aside>
      </div>
    </div>
  )
}

/** Entry to Help & Learning. Reading it grants nothing and changes no permit. */
function LearningCard({ admin }: { admin: boolean }) {
  // The signed-in person's own self-study progress (kept with their sign-in, 0012).
  const saved = useLearningProgress(tutorialContent.id, tutorialContent.version)
  return (
    <Card>
      <div className="flex items-start gap-3">
        <span className="va-support-icon" aria-hidden="true">
          <svg className="va-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="m3 7 9-4 9 4-9 4zM6 9v7c4 3 8 3 12 0V9M21 7v7" />
          </svg>
        </span>
        <div className="min-w-0">
          <h2 className="text-[17px] leading-snug font-semibold text-[#354b64]">Help &amp; Learning</h2>
          <p className="mt-2 text-sm leading-relaxed text-ink-soft">
            Study the in-house PTW programme, learn the ePTW screens and complete the knowledge assessment and observed practical.
            Programme completion and PTW appointments are recorded separately.
          </p>
          {saved.isSuccess ? (
            <p className="mt-2 text-sm text-ink" data-testid="home-learning-progress">
              <span className="font-semibold">Guided screen practice:</span> {progressSentence(saved.data?.progress ?? null)}
            </p>
          ) : null}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <LinkButton to="/learn" variant="secondary">
              Open Help &amp; Learning
            </LinkButton>
            {admin ? (
              <Link to="/learn/progress" className="tap inline-flex items-center text-sm font-semibold text-brand-dark underline">
                Everyone&apos;s screen-practice progress
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    </Card>
  )
}

function Greeting({ me }: { me: ReturnType<typeof useMe> }) {
  if (me.isLoading) {
    return (
      <Card>
        <div className="flex justify-center p-4">
          <Spinner />
        </div>
      </Card>
    )
  }
  if (!me.data) {
    return (
      <div className="space-y-2">
        <ErrorBanner error={me.error ?? new Error('Your details are not available.')} title="Could not load your details" />
        <p className="text-sm text-ink-soft">
          If this persona was moved to another device, switch persona or{' '}
          <Link to="/demo" className="font-semibold text-brand-dark underline">
            open the Demo page
          </Link>
          .
        </p>
      </div>
    )
  }
  const m = me.data
  const canStart = canStartPermit(m)
  return (
    <Card accent={m.is_synthetic ? 'synthetic' : undefined}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="va-eyebrow">Acting as</p>
          <h2 className="mt-1 text-[19px] leading-snug font-semibold break-words text-ink">{m.person.display_name}</h2>
          <p className="text-sm text-ink-soft">
            {[m.person.organisation, m.sites.map((s) => s.name).join(', ')].filter(Boolean).join(' - ')}
          </p>
        </div>
        {m.is_synthetic ? <Badge tone="synthetic">Synthetic persona</Badge> : null}
      </div>
      <div className="mt-3">
        <p className="text-[13px] font-semibold text-ink-faint">Your roles</p>
        <RoleList credentials={m.credentials} />
      </div>
      {!canStart && m.demo ? (
        <p className="mt-2 text-[13px] leading-normal text-ink-soft">
          Starting a permit needs a PA or RS licence and the site&apos;s apply grant. In the demo, act as Ahmed (PA).
        </p>
      ) : null}
      {m.demo ? (
        <p className="mt-3 text-[13px] leading-normal text-ink-soft">
          Demo sandbox <span className="code font-semibold">{m.demo.sandbox_code}</span>, persona{' '}
          <span className="code font-semibold">{m.demo.persona_code}</span>.{' '}
          <Link to="/demo" className="font-semibold text-brand-dark underline">
            Switch persona or add a device
          </Link>
        </p>
      ) : null}
    </Card>
  )
}

function RoleList({ credentials }: { credentials: Me['credentials'] }) {
  if (credentials.length === 0) {
    return <p className="mt-1 text-sm text-ink-soft">No licence or certificate on record.</p>
  }
  return (
    <ul className="mt-1 space-y-1.5">
      {credentials.map((c) => {
        // A company-wide licence ('organisation', 0021) covers every location of the company.
        const areas = c.scopes.some((s) => s.scope_mode === 'organisation' || s.scope_mode === 'explicit_global')
          ? ['every BadrEOR location']
          : c.scopes.map((s) => s.site_area?.name ?? humanise(s.scope_mode)).filter(Boolean)
        return (
          <li key={c.credential_id} className="rounded-lg border border-line bg-white px-2 py-1.5 text-sm">
            <span className="font-semibold text-ink">{roleLabel(c.role_code)}</span>
            <span className="text-ink-soft">
              {' '}
              - {credentialKindLabel(c.credential_kind)}, {c.expires_on ? `expires ${fmtDate(c.expires_on)}` : 'no stated expiry'}
            </span>{' '}
            <Badge tone={credentialStatusTone(c.status)}>{humanise(c.status)}</Badge>
            {areas.length > 0 ? <span className="block text-xs text-ink-soft">Covers: {areas.join(', ')}</span> : null}
          </li>
        )
      })}
    </ul>
  )
}

function PermitsBlock({ q, items, empty, compact }: { q: ReturnType<typeof usePermits>; items: PermitSummary[]; empty: ReactNode; compact?: boolean }) {
  if (q.isLoading) {
    return (
      <div className="flex justify-center p-4">
        <Spinner />
      </div>
    )
  }
  if (!q.data) return <ErrorBanner error={q.error ?? new Error('Permits are not available.')} title="Could not load permits" />
  return (
    <div className="space-y-2">
      {q.isError ? (
        <Banner tone="warn" title="Showing the last list loaded">
          Connection problem. This list is from {fmtDateTime(new Date(q.dataUpdatedAt).toISOString())} and may be out of date.
        </Banner>
      ) : null}
      {items.length === 0 ? (
        <EmptyState title={empty} />
      ) : (
        <ul className="space-y-2">
          {items.map((p) => (
            <li key={p.id}>
              <PermitCard p={p} compact={compact} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** The demonstration steps, folded away so they never compete with real pending work. */
function WalkthroughCard({ activePersona }: { activePersona: string | null }) {
  return (
    <Card accent="synthetic">
      <details>
        <summary className="tap flex cursor-pointer items-center justify-between gap-2 text-[17px] leading-snug font-semibold text-[#354b64]">
          <span>Suggested walkthrough</span>
          <span className="text-sm font-normal text-ink-soft">{WALKTHROUGH.length} steps</span>
        </summary>
        <p className="mt-2 mb-3 text-sm text-ink-soft">
          The synthetic Class B lifecycle, from draft to archive. Switch persona from the menu at the top for each step.
        </p>
        <ol className="space-y-2">
          {WALKTHROUGH.map((s, i) => {
            const you = activePersona ? s.personas.includes(activePersona) : false
            return (
              <li key={i} className={cx('flex gap-3 rounded-lg border p-2', you ? 'border-brand bg-brand-light' : 'border-line bg-white')}>
                <span className="code flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-dark text-xs font-bold text-white">
                  {i + 1}
                </span>
                <div className="min-w-0 text-sm">
                  <p className="font-semibold text-ink">
                    {s.who}
                    {you ? (
                      <Badge tone="brand" className="ml-2">
                        You
                      </Badge>
                    ) : null}
                  </p>
                  <p className="text-ink">{s.text}</p>
                </div>
              </li>
            )
          })}
        </ol>
        <details className="mt-3">
          <summary className="tap flex cursor-pointer items-center text-sm font-semibold text-brand-dark">Example values for the walkthrough permit</summary>
          <div className="mt-2">
            <KeyValue cols={1} items={WALKTHROUGH_DATA.map(([k, v]) => [k, v])} />
          </div>
        </details>
        <p className="mt-3 text-[13px] leading-normal text-ink-soft">
          Two devices make it clearer: join from a second phone with the sandbox code on the{' '}
          <Link to="/demo" className="font-semibold text-brand-dark underline">
            Demo page
          </Link>
          .
        </p>
      </details>
    </Card>
  )
}
